import { creatorbase } from '@round/api';
import { useState } from 'react';
import { useCreatePost } from '@round/creatorbase-helpers/api-hooks';
import { getPostCostFields } from 'Modules/Plans/Posts/helpers';

export type CreatorToAdd = {
    clientInfluencerAccountId: number;
    cost: number | null;
    currencyId: number | null;
};

export type AddCreatorsResult = {
    addedAccountIds: number[];
    failedAccountIds: number[];
};

type Params = {
    campaign: creatorbase.TiktokCampaign | creatorbase.InstagramCampaign | undefined;
    isPaymentEnabled: boolean | undefined;
};

/**
 * Creates a planned post per selected creator. Requests are independent, so a creator
 * that fails leaves the rest untouched and can be retried on its own.
 */
export function useAddCreatorsFromPool({ campaign, isPaymentEnabled }: Params) {
    const [isAdding, setIsAdding] = useState(false);
    const { createAsync } = useCreatePost();

    const addCreators = async (creators: CreatorToAdd[]): Promise<AddCreatorsResult> => {
        if (!campaign || !creators.length) {
            return { addedAccountIds: [], failedAccountIds: [] };
        }

        setIsAdding(true);

        const results = await Promise.allSettled(
            creators.map((creator) =>
                createAsync({
                    campaign_id: campaign.id,
                    client_influencer_account_id: creator.clientInfluencerAccountId,
                    ...getPostCostFields({
                        isPostCostEditable: campaign.is_post_cost_editable,
                        isPaymentEnabled,
                        campaignCurrencyId: campaign.currency_id,
                        cost: creator.cost,
                        currencyId: creator.currencyId,
                    }),
                })
            )
        );

        setIsAdding(false);

        const addedAccountIds: number[] = [];
        const failedAccountIds: number[] = [];

        results.forEach((result, index) => {
            const { clientInfluencerAccountId } = creators[index];

            if (result.status === 'fulfilled') {
                addedAccountIds.push(clientInfluencerAccountId);
            } else {
                failedAccountIds.push(clientInfluencerAccountId);
            }
        });

        return { addedAccountIds, failedAccountIds };
    };

    return { addCreators, isAdding };
}
